// Enhanced API service for communicating with the backend
// Includes retry logic, comprehensive error handling, and loading state management
// Multi-tenant support with authentication headers

import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { 
  WarehouseNode, 
  CreateNodeRequest, 
  UpdateNodeRequest, 
  NodesResponse, 
  NodeResponse,
  InventoryLevel,
  DemandData,
  Product,
  DemandGap,
  InventoryRequest,
  DemandRequest,
  InventoryResponse,
  DemandResponse,
  DemandGapResponse,
  OptimizationRequest,
  OptimizationResponse,
  SolverInfo,
  SolverInfoResponse
} from '../types';
import { configService, apiConfig } from '../config/api';
import { errorHandler, handleApiError, ApiError } from './errorHandler';
import { loadingManager, startLoading, stopLoading, updateProgress } from './loadingManager';
import { CognitoAuthService } from './cognitoAuthService';

// Create axios instance with enhanced configuration
const apiClient = axios.create({
  baseURL: apiConfig.baseUrl,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for adding authentication headers and logging
apiClient.interceptors.request.use(
  (config) => {
    // Add authentication token - use ID token instead of access token for API Gateway
    const token = CognitoAuthService.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add timestamp for request tracking
    config.metadata = { startTime: Date.now() };
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling, logging, and token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Log response time in development
    if (process.env.NODE_ENV === 'development' && response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`);
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await CognitoAuthService.refreshToken();
        const newToken = CognitoAuthService.getIdToken();
        
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        CognitoAuthService.logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Enhanced error logging
    const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : 0;
    console.error(`API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} (${duration}ms)`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    return Promise.reject(error);
  }
);

// Retry configuration
interface RetryConfig {
  attempts: number;
  delay: number;
  backoffFactor: number;
  retryCondition?: (error: any) => boolean;
}

const defaultRetryConfig: RetryConfig = {
  attempts: apiConfig.retryAttempts,
  delay: apiConfig.retryDelay,
  backoffFactor: 2,
  retryCondition: (error) => {
    // Retry on network errors, timeouts, and 5xx errors
    return !error.response || 
           error.code === 'NETWORK_ERROR' || 
           error.code === 'ECONNABORTED' ||
           (error.response.status >= 500 && error.response.status < 600) ||
           error.response.status === 429; // Rate limiting
  }
};

// Enhanced API request with retry logic
async function apiRequest<T>(
  requestFn: () => Promise<AxiosResponse<T>>,
  loadingKey?: string,
  retryConfig: RetryConfig = defaultRetryConfig
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= retryConfig.attempts + 1; attempt++) {
    try {
      // Update loading progress if tracking
      if (loadingKey && attempt > 1) {
        const progress = ((attempt - 1) / (retryConfig.attempts + 1)) * 50; // First 50% for retries
        updateProgress(loadingKey, progress, `Attempt ${attempt}...`);
      }
      
      const response = await requestFn();
      
      // Update progress to completion if tracking
      if (loadingKey) {
        updateProgress(loadingKey, 100);
      }
      
      return response.data;
    } catch (error) {
      lastError = error;
      
      // Don't retry if this is the last attempt or if retry condition is not met
      if (attempt > retryConfig.attempts || !retryConfig.retryCondition?.(error)) {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = retryConfig.delay * Math.pow(retryConfig.backoffFactor, attempt - 1);
      
      // Log retry attempt
      console.warn(`API request failed, retrying in ${delay}ms (attempt ${attempt}/${retryConfig.attempts})`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // All retries failed, throw the last error
  throw lastError;
}

export class NodeService {
  /**
   * Get all warehouse nodes
   */
  static async getNodes(): Promise<WarehouseNode[]> {
    const loadingKey = 'fetch_nodes';
    
    try {
      startLoading(loadingKey, {
        operation: 'fetch_nodes',
        message: 'Loading warehouse nodes...',
        estimatedDuration: 2000
      });

      const result = await apiRequest<NodesResponse>(
        () => apiClient.get('/api/nodes'),
        loadingKey
      );

      return result.nodes || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'fetch_nodes',
        component: 'NodeService' 
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Create a new warehouse node
   */
  static async createNode(nodeData: CreateNodeRequest): Promise<WarehouseNode> {
    const loadingKey = 'create_node';
    
    try {
      startLoading(loadingKey, {
        operation: 'create_node',
        message: 'Creating warehouse node...',
        estimatedDuration: 3000
      });

      const result = await apiRequest<NodeResponse>(
        () => apiClient.post('/api/nodes', nodeData),
        loadingKey
      );

      return result.node;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'create_node',
        component: 'NodeService',
        additionalData: { nodeData }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Update an existing warehouse node
   */
  static async updateNode(nodeId: string, nodeData: UpdateNodeRequest): Promise<WarehouseNode> {
    const loadingKey = `update_node_${nodeId}`;
    
    try {
      startLoading(loadingKey, {
        operation: 'update_node',
        message: 'Updating warehouse node...',
        estimatedDuration: 3000
      });

      const result = await apiRequest<NodeResponse>(
        () => apiClient.put(`/api/nodes/${nodeId}`, nodeData),
        loadingKey
      );

      return result.node;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'update_node',
        component: 'NodeService',
        additionalData: { nodeId, nodeData }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Delete a warehouse node
   */
  static async deleteNode(nodeId: string): Promise<void> {
    const loadingKey = `delete_node_${nodeId}`;
    
    try {
      startLoading(loadingKey, {
        operation: 'delete_node',
        message: 'Deleting warehouse node...',
        estimatedDuration: 2000
      });

      await apiRequest<void>(
        () => apiClient.delete(`/api/nodes/${nodeId}`),
        loadingKey
      );
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'delete_node',
        component: 'NodeService',
        additionalData: { nodeId }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }
}

export class InventoryService {
  /**
   * Get inventory levels for all nodes and products
   */
  static async getInventory(): Promise<InventoryLevel[]> {
    const loadingKey = 'fetch_inventory';
    
    try {
      startLoading(loadingKey, {
        operation: 'fetch_inventory',
        message: 'Loading inventory data...',
        estimatedDuration: 2000
      });

      const result = await apiRequest<InventoryResponse>(
        () => apiClient.get('/api/inventory'),
        loadingKey
      );

      return result.inventory || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'fetch_inventory',
        component: 'InventoryService' 
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Update inventory level for a specific node and product
   */
  static async updateInventory(inventoryData: InventoryRequest): Promise<InventoryLevel> {
    const loadingKey = `update_inventory_${inventoryData.node_id}_${inventoryData.product_id}`;
    
    try {
      startLoading(loadingKey, {
        operation: 'update_inventory',
        message: 'Updating inventory...',
        estimatedDuration: 1500
      });

      // Wrap the inventory data in the expected format
      const requestBody = {
        updates: [inventoryData]
      };

      const result = await apiRequest<{ updated_items: InventoryLevel[] }>(
        () => apiClient.put('/api/inventory', requestBody),
        loadingKey
      );

      return result.updated_items[0];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'update_inventory',
        component: 'InventoryService',
        additionalData: { inventoryData }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Get demand data for all nodes and products
   */
  static async getDemand(): Promise<DemandData[]> {
    const loadingKey = 'fetch_demand';
    
    try {
      startLoading(loadingKey, {
        operation: 'fetch_demand',
        message: 'Loading demand data...',
        estimatedDuration: 2000
      });

      const result = await apiRequest<DemandResponse>(
        () => apiClient.get('/api/demand'),
        loadingKey
      );

      return result.demand || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'fetch_demand',
        component: 'InventoryService' 
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Update demand data for a specific node and product
   */
  static async updateDemand(demandData: DemandRequest): Promise<DemandData> {
    const loadingKey = `update_demand_${demandData.node_id}_${demandData.product_id}`;
    
    try {
      startLoading(loadingKey, {
        operation: 'update_demand',
        message: 'Updating demand...',
        estimatedDuration: 1500
      });

      // Wrap the demand data in the expected format
      const requestBody = {
        updates: [demandData]
      };

      const result = await apiRequest<{ updated_items: DemandData[] }>(
        () => apiClient.put('/api/demand', requestBody),
        loadingKey
      );

      return result.updated_items[0];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'update_demand',
        component: 'InventoryService',
        additionalData: { demandData }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Get demand gaps (calculated demand - inventory)
   */
  static async getDemandGaps(): Promise<DemandGap[]> {
    const loadingKey = 'fetch_gaps';
    
    try {
      startLoading(loadingKey, {
        operation: 'fetch_gaps',
        message: 'Calculating demand gaps...',
        estimatedDuration: 3000
      });

      const result = await apiRequest<DemandGapResponse>(
        () => apiClient.get('/api/demand-gaps'),
        loadingKey
      );

      return result.gaps || [];
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'fetch_gaps',
        component: 'InventoryService' 
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }
}

export class ProductService {
  /**
   * Get all products dynamically from inventory and demand data
   */
  static async getProducts(): Promise<Product[]> {
    try {
      // Get all unique product IDs from inventory and demand data
      const [inventoryResponse, demandResponse] = await Promise.all([
        apiClient.get('/api/inventory'),
        apiClient.get('/api/demand')
      ]);

      const inventoryData = inventoryResponse.data.inventory || [];
      const demandData = demandResponse.data.demand || [];

      // Collect unique product IDs
      const productIds = new Set<string>();
      
      inventoryData.forEach((inv: any) => productIds.add(inv.product_id));
      demandData.forEach((dem: any) => productIds.add(dem.product_id));

      // Convert to Product objects with reasonable defaults
      const products: Product[] = Array.from(productIds).map(productId => ({
        product_id: productId,
        name: productId === 'default-product' ? 'Default Product' : 
              productId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        unit_of_measure: 'units',
        category: 'general'
      }));

      // Ensure we always have at least the default product
      if (products.length === 0) {
        products.push({
          product_id: 'default-product',
          name: 'Default Product',
          unit_of_measure: 'units',
          category: 'general'
        });
      }

      return products.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error fetching products dynamically:', error);
      // Fallback to default product if there's an error
      return [
        {
          product_id: 'default-product',
          name: 'Default Product',
          unit_of_measure: 'units',
          category: 'general'
        }
      ];
    }
  }
}

export class OptimizationService {
  /**
   * Get available solver information
   */
  static async getSolverInfo(): Promise<SolverInfo> {
    const loadingKey = 'get_solver_info';
    
    try {
      startLoading(loadingKey, {
        operation: 'get_solver_info',
        message: 'Loading solver information...',
        estimatedDuration: 2000
      });

      const result = await apiRequest<SolverInfoResponse>(
        () => apiClient.get('/api/solvers'),
        loadingKey
      );

      return result.solvers;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'get_solver_info',
        component: 'OptimizationService'
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }

  /**
   * Run optimization for purchase order allocation
   */
  static async runOptimization(optimizationData: OptimizationRequest): Promise<OptimizationResponse> {
    const loadingKey = 'run_optimization';
    
    try {
      startLoading(loadingKey, {
        operation: 'run_optimization',
        message: 'Running optimization algorithm...',
        estimatedDuration: 15000, // Optimization can take longer
        showProgress: true
      });

      // Custom retry config for optimization (fewer retries, longer timeout)
      const optimizationRetryConfig: RetryConfig = {
        attempts: 2, // Fewer retries for long-running operations
        delay: 2000,
        backoffFactor: 2,
        retryCondition: (error) => {
          // Only retry on network errors and 5xx errors, not timeouts
          return !error.response || 
                 error.code === 'NETWORK_ERROR' || 
                 (error.response.status >= 500 && error.response.status < 600);
        }
      };

      const result = await apiRequest<OptimizationResponse>(
        () => apiClient.post('/api/optimize', optimizationData),
        loadingKey,
        optimizationRetryConfig
      );

      return result;
    } catch (error) {
      const apiError = handleApiError(error, { 
        operation: 'run_optimization',
        component: 'OptimizationService',
        additionalData: { optimizationData }
      });
      throw apiError;
    } finally {
      stopLoading(loadingKey);
    }
  }
}

// Configuration and health check utilities
export class ApiHealthService {
  /**
   * Check API health and configuration
   */
  static async checkHealth(): Promise<{
    healthy: boolean;
    configured: boolean;
    message: string;
    timestamp: string;
  }> {
    try {
      const response = await apiClient.get('/api/health', { timeout: 5000 });
      return {
        healthy: true,
        configured: configService.isConfigured(),
        message: 'API is healthy and responding',
        timestamp: response.data.timestamp || new Date().toISOString()
      };
    } catch (error) {
      return {
        healthy: false,
        configured: configService.isConfigured(),
        message: 'API is not responding or not configured',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Get configuration status
   */
  static getConfigurationStatus() {
    return configService.getConfigurationStatus();
  }
}

export default apiClient;